import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Ticket, Wrench, AlertTriangle } from "lucide-react";

export default function Hoy() {
    const [data, setData] = useState({ reservas: [], visitas: [], incidencias_activas: [], avisos_urgentes: [] });
    const [salas, setSalas] = useState([]);
    const [unidades, setUnidades] = useState([]);

    useEffect(() => {
        api.get("/dashboard/hoy").then((r) => setData(r.data));
        api.get("/salas").then((r) => setSalas(r.data));
        api.get("/unidades").then((r) => setUnidades(r.data));
        const id = setInterval(() => api.get("/dashboard/hoy").then((r) => setData(r.data)), 45_000);
        return () => clearInterval(id);
    }, []);

    const salaName = (id) => salas.find((s) => s.sala_id === id)?.nombre || "—";
    const uniName = (id) => unidades.find((u) => u.unidad_id === id)?.etiqueta || "—";

    return (
        <div>
            <PageHeader title="Hoy" subtitle={`Panorama de ${data.fecha || ""}`} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Reservas" value={data.reservas.length} icon={CalendarDays} testId="stat-reservas" />
                <StatCard label="Visitas" value={data.visitas.length} icon={Ticket} accent="success" testId="stat-visitas" />
                <StatCard label="Incidencias activas" value={data.incidencias_activas.length} icon={Wrench} accent="warning" testId="stat-incidencias" />
                <StatCard label="Avisos urgentes" value={data.avisos_urgentes.length} icon={AlertTriangle} accent="destructive" testId="stat-avisos" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <section>
                    <h3 className="font-heading text-lg mb-2">Reservas en curso / próximas</h3>
                    <div className="grid gap-2">
                        {data.reservas.length === 0 && <p className="text-sm text-muted-foreground">Sin reservas para hoy.</p>}
                        {data.reservas.map((r) => (
                            <Card key={r.reserva_id}><CardContent className="p-3 flex items-center gap-3">
                                <div className="font-mono text-primary w-20">{r.hora_inicio}–{r.hora_fin}</div>
                                <div className="flex-1">
                                    <p className="font-heading font-semibold text-sm">{salaName(r.sala_id)}</p>
                                    <p className="text-xs text-muted-foreground">{uniName(r.unidad_id)} · {r.motivo || "—"}</p>
                                </div>
                                <Badge>{r.estado}</Badge>
                            </CardContent></Card>
                        ))}
                    </div>
                </section>

                <section>
                    <h3 className="font-heading text-lg mb-2">Visitas esperadas</h3>
                    <div className="grid gap-2">
                        {data.visitas.length === 0 && <p className="text-sm text-muted-foreground">Sin visitas para hoy.</p>}
                        {data.visitas.map((v) => (
                            <Card key={v.visita_id}><CardContent className="p-3 flex items-center gap-3">
                                <div className="font-mono text-primary w-20">{v.hora_estimada || "—"}</div>
                                <div className="flex-1">
                                    <p className="font-heading font-semibold text-sm">{v.nombre_visita}</p>
                                    <p className="text-xs text-muted-foreground">{uniName(v.unidad_id)} · {v.anfitrion_nombre}</p>
                                </div>
                                <Badge>{v.estado.replace("_", " ")}</Badge>
                            </CardContent></Card>
                        ))}
                    </div>
                </section>

                <section>
                    <h3 className="font-heading text-lg mb-2">Incidencias activas</h3>
                    <div className="grid gap-2">
                        {data.incidencias_activas.length === 0 && <p className="text-sm text-muted-foreground">Sin incidencias activas.</p>}
                        {data.incidencias_activas.slice(0, 8).map((i) => (
                            <Card key={i.incidencia_id}><CardContent className="p-3">
                                <div className="flex items-center justify-between">
                                    <p className="font-heading font-semibold text-sm">{i.titulo}</p>
                                    <Badge>{i.estado.replace("_", " ")}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{uniName(i.unidad_id)} · {i.categoria}</p>
                            </CardContent></Card>
                        ))}
                    </div>
                </section>

                <section>
                    <h3 className="font-heading text-lg mb-2">Avisos urgentes</h3>
                    <div className="grid gap-2">
                        {data.avisos_urgentes.length === 0 && <p className="text-sm text-muted-foreground">Sin avisos urgentes vigentes.</p>}
                        {data.avisos_urgentes.map((a) => (
                            <Card key={a.comunicacion_id} className="border-destructive/40"><CardContent className="p-3">
                                <p className="font-heading font-semibold text-sm">{a.titulo}</p>
                                <p className="text-xs text-muted-foreground">{a.mensaje}</p>
                            </CardContent></Card>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
